import { forwardRef, type ReactNode } from 'react'

import { cn } from '../../../utils/cn'
import { Button, type ButtonProps } from '../../atoms/button/button'
import { Text } from '../../atoms/text/text'
import { TextLink, type TextLinkProps } from '../../atoms/text-link/text-link'

import type { OmniTableCellContext } from './omni-table'
import { OmniTableIcon } from './omni-table-icon'

interface CellPresentationProps {
  context?: OmniTableCellContext
  className?: string
}

/** Status emphasis a page gives a whole row, such as an overdue or due-today bill. */
export type OmniTableCellTone = 'danger' | 'warning'

/** Text colour for a toned cell; pages apply it to plain-text cells in the same row. */
export const omniTableCellToneClassName: Record<OmniTableCellTone, string> = {
  danger: 'text-danger-icon',
  warning: 'text-warning-icon'
}

export interface OmniTableIconButtonProps
  extends Omit<ButtonProps, 'children' | 'size' | 'variant' | 'iconOnly'> {
  icon: string
  'aria-label': string
}

/** Compact cell adornment; keeps Acqua button states, motion, focus and tooltip composition. */
export const OmniTableIconButton = forwardRef<
  HTMLButtonElement,
  OmniTableIconButtonProps
>(({ icon, className, ...props }, ref) => (
  <Button
    {...props}
    ref={ref}
    variant='secondary'
    size='sm'
    iconOnly
    className={cn(
      'size-(--text-body-lg--line-height) rounded-[2px] p-1',
      className
    )}
  >
    <OmniTableIcon
      name={icon}
      className='text-[length:calc(var(--text-body-lg--line-height)-8px)]/none size-[calc(var(--text-body-lg--line-height)-8px)] shrink-0'
    />
  </Button>
))
OmniTableIconButton.displayName = 'OmniTableIconButton'

export interface OmniTableLinkCellProps
  extends Omit<TextLinkProps, 'children' | 'variant' | 'weight'>,
    CellPresentationProps {
  href: string
  children: string | number
  /** Replaces the link blue; the underline and weight stay. */
  tone?: OmniTableCellTone
}

/**
 * Uses Acqua's blue, underlined link and preserves native link navigation.
 * It forwards its ref, so a router's link component can own navigation.
 */
export const OmniTableLinkCell = forwardRef<
  HTMLAnchorElement,
  OmniTableLinkCellProps
>(({ children, context, tone, className, ...props }, ref) => (
  <TextLink
    {...props}
    ref={ref}
    variant='body-lg'
    className={cn(tone && omniTableCellToneClassName[tone], className)}
  >
    {context ? context.highlight(children) : children}
  </TextLink>
))
OmniTableLinkCell.displayName = 'OmniTableLinkCell'

function DetailCell({
  primary,
  secondary,
  tone,
  className
}: {
  primary: ReactNode
  secondary?: ReactNode
  tone?: OmniTableCellTone
  className?: string
}) {
  return (
    <div className={cn('flex flex-col whitespace-nowrap text-left', className)}>
      <Text
        as='span'
        variant='body-lg'
        weight='semibold'
        className={
          tone ? omniTableCellToneClassName[tone] : 'text-surface-title-50'
        }
      >
        {primary}
      </Text>
      {secondary && (
        <Text
          as='span'
          variant='caption-lg'
          weight='semibold'
          className={
            tone ? omniTableCellToneClassName[tone] : 'text-surface-body'
          }
        >
          {secondary}
        </Text>
      )}
    </div>
  )
}

export interface OmniTableDateCellProps extends CellPresentationProps {
  /** Date-only ISO values retain their calendar date in the user's local timezone. */
  value: Date | string | null | undefined
  /** The page can supply a stable clock for SSR and previews. */
  referenceDate?: Date
  /** Consumer-owned indicator or tooltip beside the date, without changing its relative label. */
  trailing?: ReactNode
  /** Colours both the date and its relative label. */
  tone?: OmniTableCellTone
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: '2-digit',
  year: 'numeric'
})

function parseDate(value: OmniTableDateCellProps['value']): Date | undefined {
  if (value == null) return undefined
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? undefined : value
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  const date = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value)
  if (Number.isNaN(date.getTime())) return undefined
  if (
    match &&
    (date.getFullYear() !== Number(match[1]) ||
      date.getMonth() !== Number(match[2]) - 1 ||
      date.getDate() !== Number(match[3]))
  )
    return undefined
  return date
}

const calendarDay = (date: Date) =>
  Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000

/** Calendar-day differences avoid off-by-one labels around midnight or daylight saving. */
export function OmniTableDateCell({
  value,
  referenceDate = new Date(),
  context,
  className,
  trailing,
  tone
}: OmniTableDateCellProps) {
  const date = parseDate(value)
  if (!date) return <DetailCell primary='—' className={className} />
  const days = calendarDay(date) - calendarDay(referenceDate)
  const duration = `${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'}`
  const relative = Number.isNaN(days)
    ? undefined
    : days > 0
    ? `in ${duration}`
    : `${duration} ago`
  const label = dateFormatter.format(date)
  return (
    <DetailCell
      className={className}
      tone={tone}
      primary={
        <span className='inline-flex items-center gap-2'>
          <time dateTime={date.toISOString()}>
            {context ? context.highlight(label) : label}
          </time>
          {trailing}
        </span>
      }
      secondary={relative && (context ? context.highlight(relative) : relative)}
    />
  )
}

export type OmniTablePaymentCellProps = CellPresentationProps &
  (
    | {
        kind: 'bank'
        /** Payment rail, such as ACH or Wire. */
        type: string
        last4: string
        bankName?: string
        accountType?: string
      }
    | {
        kind: 'card'
        /** Defaults to Credit card; can also describe a debit card. */
        type?: string
        last4: string
        brand?: string
      }
  )

/** Shared payment presentation; only the final four digits are displayed. */
export function OmniTablePaymentCell(props: OmniTablePaymentCellProps) {
  const { context, className } = props
  const type = props.type ?? 'Credit card'
  const digits = props.last4.replace(/\D/g, '').slice(-4)
  const account = digits.length === 4 ? `****${digits}` : '—'
  const primary = `${type} | ${account}`
  const secondary =
    props.kind === 'bank'
      ? [props.accountType, props.bankName].filter(Boolean).join(' | ')
      : props.brand
  return (
    <DetailCell
      className={className}
      primary={context ? context.highlight(primary) : primary}
      secondary={
        secondary && (context ? context.highlight(secondary) : secondary)
      }
    />
  )
}
